import React from 'react';

import { Box, Button } from '@mui/material';

import { Field, Form, TextField } from '@hrlogics/react-components/form';
import { SelectFieldOption } from '@hrlogics/react-helper-types';

import { CompanyType, GetPartnershipsDocument, useSendInviteToPartnershipMutation } from 'src/api';
import { SelectField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { FormField } from './constants';
import { FormValues } from './types';
import { formValidationSchema } from './validation';

interface InvitePartnershipByPlatformStaffModalContentProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InvitePartnershipByPlatformStaffModalContent: React.FC<
  InvitePartnershipByPlatformStaffModalContentProps
> = ({ closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [sendInvite] = useSendInviteToPartnershipMutation({
    context: createApolloContext({
      errorMessage: 'Failed to invite a partner organization',
      successMessage: 'Partner organization successfully invited',
    }),
    refetchQueries: [GetPartnershipsDocument],
  });

  const onSubmit = React.useCallback(
    async (formValues: FormValues) => {
      switchClosability(false);
      try {
        await sendInvite({
          variables: {
            companyType: formValues.companyType,
            staffEmail: formValues.email,
          },
        });

        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [closeModal, sendInvite, switchClosability],
  );

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={formValidationSchema}
      subscription={{ valid: true, submitting: true }}
      containerProps={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Box>
              <Field
                name={FormField.companyType}
                component={SelectField}
                label="Partnership Type"
                options={companyTypeOptions}
                fullWidth
                required
              />
            </Box>

            <Box>
              <Field
                name={FormField.email}
                component={TextField}
                label="Email address"
                fullWidth
                required
              />
            </Box>

            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button variant="outlined" color="error" onClick={closeModal} disabled={submitting}>
                Close
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting}
                loading={submitting}
              >
                Send invite
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const initialValues = {
  companyType: CompanyType.Peo,
} as const satisfies Partial<FormValues>;

const companyTypeOptions: SelectFieldOption<CompanyType>[] = [
  { label: 'Employer only Partner', value: CompanyType.Peo },
  { label: 'Employer & PEO Partner', value: CompanyType.PeoPayrollProvider },
];
